/**
 * WysiwygRenderer - WYSIWYG 模式渲染元件
 *
 * 將 Markdown AST 渲染為格式化的 HTML 內容
 *
 * @module editor/components/WysiwygRenderer
 * @see Requirements 3.4
 */

import React, { useMemo, useCallback, useState } from 'react';
import type { RootContent, PhrasingContent } from 'mdast';
import { parseMarkdown } from '../ast/parse';
import type { MarkdownAST } from '../ast/types';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useTypedTranslation } from '../../i18n';

// ============================================================================
// Types
// ============================================================================

export interface WysiwygRendererProps {
  /** Markdown 內容 */
  markdown: string;
  /** 自訂 className */
  className?: string;
  /** 點擊事件回調 (用於定位游標) */
  onClick?: (offset: number) => void;
}

interface RenderContext {
  onClick?: (offset: number) => void;
  t: (key: string) => string;
}

// 用於追蹤渲染位置的 data attribute 名稱
const DATA_OFFSET_START = 'data-offset-start';
const DATA_OFFSET_END = 'data-offset-end';

// ============================================================================
// Styles
// ============================================================================

const styles = {
  container: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
    lineHeight: 1.6,
    color: '#1f2937',
    padding: '16px',
    minHeight: '100%',
  },
  heading1: {
    fontSize: '2em',
    fontWeight: 700,
    marginTop: '0.67em',
    marginBottom: '0.67em',
    lineHeight: 1.2,
  },
  heading2: {
    fontSize: '1.5em',
    fontWeight: 700,
    marginTop: '0.83em',
    marginBottom: '0.83em',
    lineHeight: 1.3,
  },
  heading3: {
    fontSize: '1.17em',
    fontWeight: 700,
    marginTop: '1em',
    marginBottom: '1em',
    lineHeight: 1.4,
  },
  heading4: {
    fontSize: '1em',
    fontWeight: 700,
    marginTop: '1.33em',
    marginBottom: '1.33em',
  },
  heading5: {
    fontSize: '0.83em',
    fontWeight: 700,
    marginTop: '1.67em',
    marginBottom: '1.67em',
  },
  heading6: {
    fontSize: '0.67em',
    fontWeight: 700,
    marginTop: '2.33em',
    marginBottom: '2.33em',
  },
  paragraph: {
    marginTop: '1em',
    marginBottom: '1em',
  },
  blockquote: {
    borderLeft: '4px solid #e5e7eb',
    paddingLeft: '16px',
    marginLeft: 0,
    marginRight: 0,
    color: '#6b7280',
    fontStyle: 'italic' as const,
  },
  codeBlockWrapper: {
    borderRadius: '6px',
    overflow: 'hidden',
    marginTop: '12px',
    marginBottom: '12px',
    border: '1px solid #374151',
  },
  codeBlockHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#1f2937',
    padding: '8px 16px',
    borderBottom: '1px solid #374151',
  },
  langLabel: {
    fontSize: '0.75em',
    fontFamily: 'ui-monospace, monospace',
    fontWeight: 'bold' as const,
    color: '#9ca3af',
    letterSpacing: '0.05em',
  },
  copyButton: {
    backgroundColor: 'transparent',
    border: '1px solid #4b5563',
    borderRadius: '4px',
    padding: '4px 12px',
    fontSize: '0.75em',
    color: '#d1d5db',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  codeBlock: {
    backgroundColor: '#f3f4f6',
    padding: '12px 16px',
    borderRadius: '6px',
    fontFamily: 'ui-monospace, monospace',
    fontSize: '0.875em',
    overflow: 'auto' as const,
    whiteSpace: 'pre' as const,
  },
  inlineCode: {
    backgroundColor: '#f3f4f6',
    padding: '2px 6px',
    borderRadius: '4px',
    fontFamily: 'ui-monospace, monospace',
    fontSize: '0.875em',
  },
  list: {
    paddingLeft: '24px',
    marginTop: '1em',
    marginBottom: '1em',
  },
  listItem: {
    marginTop: '0.25em',
    marginBottom: '0.25em',
  },
  link: {
    color: '#2563eb',
    textDecoration: 'underline' as const,
  },
  image: {
    maxWidth: '100%',
    height: 'auto',
  },
  thematicBreak: {
    border: 'none',
    borderTop: '1px solid #e5e7eb',
    marginTop: '2em',
    marginBottom: '2em',
  },
  frontmatter: {
    backgroundColor: '#fef3c7',
    padding: '12px 16px',
    borderRadius: '6px',
    fontFamily: 'ui-monospace, monospace',
    fontSize: '0.875em',
    marginBottom: '1em',
    color: '#92400e',
  },
};

const headingStyles: Record<number, React.CSSProperties> = {
  1: styles.heading1,
  2: styles.heading2,
  3: styles.heading3,
  4: styles.heading4,
  5: styles.heading5,
  6: styles.heading6,
};

// ============================================================================
// Code Block Component
// ============================================================================

/**
 * Code Block 組件 - 支援語言標籤、複製按鈕和語法高亮
 */
const CodeBlock: React.FC<{
  value: string;
  lang?: string | null;
  positionAttrs: Record<string, number>;
}> = ({ value, lang, positionAttrs }) => {
  const [copied, setCopied] = useState(false);
  const language = lang || 'text';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  return (
    <div style={styles.codeBlockWrapper} {...positionAttrs}>
      {/* Header: Language Label + Copy Button */}
      <div style={styles.codeBlockHeader}>
        <span style={styles.langLabel}>{language.toUpperCase()}</span>
        <button
          style={styles.copyButton}
          onClick={handleCopy}
          type="button"
          title="複製代碼"
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#374151';
            e.currentTarget.style.borderColor = '#6b7280';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = '#4b5563';
          }}
        >
          {copied ? '✓ 已複製' : '📋 複製'}
        </button>
      </div>

      {/* Syntax Highlighted Code */}
      <SyntaxHighlighter
        language={language}
        style={oneDark}
        customStyle={{
          margin: 0,
          borderRadius: '0 0 6px 6px',
          fontSize: '0.875em',
        }}
        PreTag="div"
      >
        {value}
      </SyntaxHighlighter>
    </div>
  );
};

// ============================================================================
// Render Functions
// ============================================================================

/**
 * 渲染 Phrasing Content (行內內容)
 */
function renderPhrasingContent(nodes: PhrasingContent[], ctx: RenderContext): React.ReactNode[] {
  return nodes.map((node, index) => renderPhrasingNode(node, index, ctx));
}

/**
 * 渲染單個 Phrasing Node
 */
function renderPhrasingNode(
  node: PhrasingContent,
  key: number,
  ctx: RenderContext
): React.ReactNode {
  switch (node.type) {
    case 'text':
      return <span key={key}>{node.value}</span>;

    case 'emphasis':
      return <em key={key}>{renderPhrasingContent(node.children as PhrasingContent[], ctx)}</em>;

    case 'strong':
      return (
        <strong key={key}>{renderPhrasingContent(node.children as PhrasingContent[], ctx)}</strong>
      );

    case 'inlineCode':
      return (
        <code key={key} style={styles.inlineCode}>
          {node.value}
        </code>
      );

    case 'link':
      return (
        <a
          key={key}
          href={node.url}
          title={node.title || undefined}
          style={styles.link}
          target="_blank"
          rel="noopener noreferrer"
        >
          {renderPhrasingContent(node.children as PhrasingContent[], ctx)}
        </a>
      );

    case 'image':
      return (
        <img
          key={key}
          src={node.url}
          alt={node.alt || ''}
          title={node.title || undefined}
          style={styles.image}
        />
      );

    case 'break':
      return <br key={key} />;

    default:
      // 未知類型，嘗試渲染 value 或 children
      if ('value' in node && typeof node.value === 'string') {
        return <span key={key}>{node.value}</span>;
      }
      if ('children' in node && Array.isArray(node.children)) {
        return (
          <span key={key}>{renderPhrasingContent(node.children as PhrasingContent[], ctx)}</span>
        );
      }
      return null;
  }
}

/**
 * 渲染 Block Content (區塊內容)
 */
function renderBlockContent(node: RootContent, key: number, ctx: RenderContext): React.ReactNode {
  // 取得節點的位置資訊
  const offsetStart = node.position?.start?.offset ?? 0;
  const offsetEnd = node.position?.end?.offset ?? 0;
  const positionAttrs = {
    [DATA_OFFSET_START]: offsetStart,
    [DATA_OFFSET_END]: offsetEnd,
  };

  switch (node.type) {
    case 'heading': {
      const HeadingTag = `h${node.depth}` as keyof JSX.IntrinsicElements;
      const headingStyle = headingStyles[node.depth] || styles.heading1;
      return (
        <HeadingTag key={key} style={headingStyle} {...positionAttrs}>
          {renderPhrasingContent(node.children as PhrasingContent[], ctx)}
        </HeadingTag>
      );
    }

    case 'paragraph':
      return (
        <p key={key} style={styles.paragraph} {...positionAttrs}>
          {renderPhrasingContent(node.children as PhrasingContent[], ctx)}
        </p>
      );

    case 'blockquote':
      return (
        <blockquote key={key} style={styles.blockquote} {...positionAttrs}>
          {node.children.map((child, i) => renderBlockContent(child, i, ctx))}
        </blockquote>
      );

    case 'code':
      return (
        <CodeBlock key={key} value={node.value} lang={node.lang} positionAttrs={positionAttrs} />
      );

    case 'list': {
      const ListTag = node.ordered ? 'ol' : 'ul';
      return (
        <ListTag key={key} style={styles.list} start={node.start || undefined} {...positionAttrs}>
          {node.children.map((item, i) => {
            const itemOffsetStart = item.position?.start?.offset ?? 0;
            const itemOffsetEnd = item.position?.end?.offset ?? 0;
            return (
              <li
                key={i}
                style={styles.listItem}
                data-offset-start={itemOffsetStart}
                data-offset-end={itemOffsetEnd}
              >
                {item.children.map((child, j) => renderBlockContent(child as RootContent, j, ctx))}
              </li>
            );
          })}
        </ListTag>
      );
    }

    case 'thematicBreak':
      return <hr key={key} style={styles.thematicBreak} {...positionAttrs} />;

    case 'html':
      // 安全考量：不直接渲染 HTML
      return (
        <div
          key={key}
          style={styles.codeBlock}
          dangerouslySetInnerHTML={{ __html: '' }}
          {...positionAttrs}
        >
          {node.value}
        </div>
      );

    case 'yaml':
      // Frontmatter
      return (
        <div key={key} style={styles.frontmatter} {...positionAttrs}>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>{ctx.t('wysiwyg.frontmatter')}</div>
          <pre style={{ margin: 0 }}>{node.value}</pre>
        </div>
      );

    default:
      // 未知類型
      if ('children' in node && Array.isArray(node.children)) {
        return (
          <div key={key} {...positionAttrs}>
            {(node.children as RootContent[]).map((child, i) => renderBlockContent(child, i, ctx))}
          </div>
        );
      }
      return null;
  }
}

// ============================================================================
// Component
// ============================================================================

/**
 * WYSIWYG 渲染元件
 *
 * 將 Markdown 內容解析為 AST 並渲染為格式化的 HTML
 *
 * @example
 * ```tsx
 * <WysiwygRenderer
 *   markdown="# Hello\n\nWorld"
 *   onClick={(offset) => console.log('Clicked at', offset)}
 * />
 * ```
 */
export const WysiwygRenderer: React.FC<WysiwygRendererProps> = ({
  markdown,
  className,
  onClick,
}) => {
  const { t } = useTypedTranslation();

  // 解析 Markdown 為 AST
  const ast = useMemo<MarkdownAST>(() => {
    try {
      return parseMarkdown(markdown);
    } catch {
      // 解析失敗時返回空 AST
      return { type: 'root', children: [] };
    }
  }, [markdown]);

  // 渲染上下文
  const ctx: RenderContext = useMemo(
    () => ({ onClick, t: (key: string) => t(key as Parameters<typeof t>[0]) }),
    [onClick, t]
  );

  // 處理點擊事件 - 計算點擊位置對應的 offset
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!onClick) return;

      // 嘗試從點擊的元素或其父元素找到 offset 資訊
      let target = e.target as HTMLElement | null;
      let offsetStart = 0;

      while (target && target !== e.currentTarget) {
        const startAttr = target.getAttribute(DATA_OFFSET_START);
        if (startAttr !== null) {
          offsetStart = parseInt(startAttr, 10);
          break;
        }
        target = target.parentElement;
      }

      onClick(offsetStart);
    },
    [onClick]
  );

  return (
    <div
      className={className}
      style={styles.container}
      onClick={handleClick}
      data-testid="wysiwyg-renderer"
      role="document"
      aria-label="Formatted document view"
      aria-readonly="true"
    >
      {ast.children.map((node, index) => renderBlockContent(node as RootContent, index, ctx))}
    </div>
  );
};

// ============================================================================
// Accessibility Styles for WYSIWYG Renderer (injected once)
// ============================================================================

if (typeof document !== 'undefined') {
  const styleId = 'wysiwyg-renderer-a11y';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      /* High Contrast Mode for WYSIWYG (Requirements 9.5, 9.6) */
      @media (prefers-contrast: more) {
        [data-testid="wysiwyg-renderer"] {
          color: #000 !important;
          background-color: #fff !important;
        }

        [data-testid="wysiwyg-renderer"] a {
          color: #00008b !important;
          text-decoration: underline !important;
        }

        [data-testid="wysiwyg-renderer"] code,
        [data-testid="wysiwyg-renderer"] pre {
          background-color: #f0f0f0 !important;
          border: 1px solid #000 !important;
        }

        [data-testid="wysiwyg-renderer"] blockquote {
          border-left-color: #000 !important;
          color: #000 !important;
        }

        [data-testid="wysiwyg-renderer"] hr {
          border-top-color: #000 !important;
        }

        /* Code block header and button in high contrast */
        [data-testid="wysiwyg-renderer"] button {
          border: 2px solid #000 !important;
          color: #000 !important;
        }
      }

      /* Reduced Motion for WYSIWYG (Requirements 9.7) */
      @media (prefers-reduced-motion: reduce) {
        [data-testid="wysiwyg-renderer"] *,
        [data-testid="wysiwyg-renderer"] *::before,
        [data-testid="wysiwyg-renderer"] *::after {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
        }
      }

      /* Focus styles for interactive elements */
      [data-testid="wysiwyg-renderer"] a:focus-visible {
        outline: 2px solid #2563eb;
        outline-offset: 2px;
      }

      @media (prefers-contrast: more) {
        [data-testid="wysiwyg-renderer"] a:focus-visible {
          outline: 3px solid #000;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

export default WysiwygRenderer;
